import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  CreditCard,
  FileText,
  Shield,
  AlertCircle,
  Loader2,
  Send,
  Building2,
  Mail,
  Banknote,
  Coins,
  ChevronDown,
  Copy,
  Check,
} from "lucide-react";
import { publicPayApi } from "../services/api";

// ─── Types ───────────────────────────────────────────────────────────────────

interface LineItem {
  description: string;
  amountCents: number;
}

interface PaymentMethods {
  accepted: string[];
  etransfer?: { email: string; recipientName?: string; notes?: string };
  wire?: { bankName?: string; accountName?: string; institutionNumber?: string; transitNumber?: string; accountNumber?: string; swiftCode?: string; notes?: string };
  cheque?: { payableTo?: string; mailingAddress?: string; notes?: string };
  eft?: { institutionNumber?: string; transitNumber?: string; accountNumber?: string; notes?: string };
  cash?: boolean;
}

interface InvoiceData {
  firmName: string;
  invoiceNumber: string;
  dueAt?: string | null;
  currency: string;
  lineItems: LineItem[];
  subtotalCents?: number;
  discountCents?: number;
  taxCents?: number;
  totalCents: number;
  paidCents: number;
  balanceDueCents: number;
  status: string;
  paymentEnabled?: boolean;
  paymentMethods?: PaymentMethods;
  preferredMethod?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (cents: number | null | undefined, currency = "CAD") => {
  const amount = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: (currency || "CAD").toUpperCase(),
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const KavoaHeader: React.FC = () => (
  <div className="bg-white border-b border-slate-200">
    <div className="max-w-lg mx-auto px-6 py-4">
      <div className="flex items-center gap-2">
        <div
          className="h-8 w-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: "var(--color-primary)" }}
        >
          <span className="text-white font-bold text-sm">K</span>
        </div>
        <span className="font-bold text-slate-800">Kavoa</span>
      </div>
    </div>
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <div className="h-5 bg-slate-200 rounded w-2/3" />
      <div className="h-4 bg-slate-200 rounded w-1/2" />
      <div className="h-4 bg-slate-200 rounded w-1/3" />
      <div className="border-t border-slate-100 my-4" />
      <div className="space-y-3">
        <div className="h-4 bg-slate-200 rounded w-full" />
        <div className="h-4 bg-slate-200 rounded w-full" />
        <div className="h-4 bg-slate-200 rounded w-3/4" />
      </div>
      <div className="border-t border-slate-100 my-4" />
      <div className="h-6 bg-slate-200 rounded w-1/3 ml-auto" />
    </div>
    <div className="h-12 bg-slate-200 rounded-xl" />
  </div>
);

const AlreadyPaidView: React.FC<{ invoice: InvoiceData }> = ({ invoice }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="text-center space-y-4"
  >
    <div className="flex justify-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
      >
        <CheckCircle className="h-16 w-16 text-emerald-500" />
      </motion.div>
    </div>
    <h1 className="text-2xl font-bold text-slate-800">Already Paid</h1>
    <p className="text-slate-500">
      Invoice {invoice.invoiceNumber} has already been paid.
    </p>
    <p className="text-sm text-slate-400">You can safely close this page.</p>
  </motion.div>
);

const InvalidLinkView: React.FC = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="text-center space-y-4"
  >
    <div className="flex justify-center">
      <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center">
        <AlertCircle className="h-8 w-8 text-red-500" />
      </div>
    </div>
    <h1 className="text-2xl font-bold text-slate-800">Link Not Valid</h1>
    <p className="text-slate-500">
      This payment link is no longer valid. It may have expired or already been
      used.
    </p>
    <p className="text-sm text-slate-400">
      Please contact the firm if you believe this is an error.
    </p>
  </motion.div>
);

const SuccessView: React.FC<{ token: string }> = ({ token }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [invoice, setInvoice] = useState<{
    invoiceNumber: string;
    amountPaidCents: number;
    currency: string;
  } | null>(null);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 15; // 30 seconds at 2-second intervals

    const poll = async () => {
      try {
        const res = await publicPayApi.getStatus(token);
        if (res.data.status === "paid") {
          setInvoice({
            invoiceNumber: res.data.invoiceNumber,
            amountPaidCents: res.data.paidCents ?? res.data.amountPaidCents ?? 0,
            currency: res.data.currency || "CAD",
          });
          setConfirmed(true);
          return;
        }
      } catch {
        // Ignore errors during polling
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        // Assume success after timeout  -  webhook may just be slow
        setConfirmed(true);
      }
    };

    poll();
  }, [token]);

  if (!confirmed) {
    return (
      <div className="text-center space-y-4 py-10">
        <Loader2 className="h-10 w-10 text-emerald-600 animate-spin mx-auto" />
        <p className="text-slate-500">Confirming your payment...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="text-center space-y-5"
    >
      <div className="flex justify-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.15 }}
        >
          <CheckCircle className="h-20 w-20 text-emerald-500" />
        </motion.div>
      </div>
      <h1 className="text-2xl font-bold text-slate-800">Payment Received</h1>
      {invoice && (
        <div className="space-y-1">
          <p className="text-slate-600">
            Invoice {invoice.invoiceNumber}
          </p>
          <p className="text-lg font-semibold text-slate-800">
            {formatCurrency(invoice.amountPaidCents, invoice.currency)}
          </p>
        </div>
      )}
      <p className="text-slate-500">
        Thank you for your payment. You will receive a confirmation email
        shortly.
      </p>
      <p className="text-sm text-slate-400">You can safely close this page.</p>
    </motion.div>
  );
};

// ─── Alternative Payment Methods ─────────────────────────────────────────────

const METHOD_LABELS: Record<string, { label: string; Icon: React.FC<any> }> = {
  etransfer: { label: "Interac e-Transfer", Icon: Send },
  wire: { label: "Wire Transfer", Icon: Building2 },
  cheque: { label: "Cheque", Icon: Mail },
  eft: { label: "EFT / Direct Deposit", Icon: Banknote },
  cash: { label: "Cash", Icon: Coins },
};

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors ml-2">
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
};

const AlternativePaymentMethods: React.FC<{
  paymentMethods?: PaymentMethods;
  preferredMethod?: string | null;
}> = ({ paymentMethods, preferredMethod }) => {
  const [open, setOpen] = useState(false);

  if (!paymentMethods) return null;

  const altMethods = paymentMethods.accepted.filter((m) => m !== "credit_card");
  if (altMethods.length === 0) return null;

  // Check if any methods actually have details
  const hasAnyDetails = altMethods.some((m) => {
    if (m === "etransfer") return !!paymentMethods.etransfer?.email;
    if (m === "wire") return !!paymentMethods.wire?.bankName;
    if (m === "cheque") return !!paymentMethods.cheque?.payableTo;
    if (m === "eft") return !!paymentMethods.eft?.institutionNumber;
    if (m === "cash") return !!paymentMethods.cash;
    return false;
  });

  if (!hasAnyDetails) return null;

  // Sort: preferred first
  const ordered = preferredMethod && altMethods.includes(preferredMethod)
    ? [preferredMethod, ...altMethods.filter((m) => m !== preferredMethod)]
    : altMethods;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="text-sm font-semibold text-slate-700">Other Ways to Pay</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-t border-slate-100">
          {ordered.map((method) => {
            const meta = METHOD_LABELS[method];
            if (!meta) return null;
            const { label, Icon } = meta;
            const isPreferred = method === preferredMethod;

            return (
              <div key={method} className="px-5 py-4 border-b border-slate-100 last:border-b-0">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-800">{label}</span>
                  {isPreferred && (
                    <span className="text-[10px] font-bold bg-brand text-white px-1.5 py-0.5 rounded">PREFERRED</span>
                  )}
                </div>

                {method === "etransfer" && paymentMethods.etransfer && (
                  <div className="text-sm space-y-1 pl-6">
                    <p className="text-slate-700">
                      Send to: <strong>{paymentMethods.etransfer.email}</strong>
                      <CopyButton text={paymentMethods.etransfer.email} />
                    </p>
                    {paymentMethods.etransfer.recipientName && (
                      <p className="text-slate-500 text-xs">Recipient: {paymentMethods.etransfer.recipientName}</p>
                    )}
                    {paymentMethods.etransfer.notes && (
                      <p className="text-slate-500 text-xs">{paymentMethods.etransfer.notes}</p>
                    )}
                  </div>
                )}

                {method === "wire" && paymentMethods.wire && (
                  <div className="text-xs space-y-1 pl-6 text-slate-700">
                    {paymentMethods.wire.bankName && <p>Bank: <strong>{paymentMethods.wire.bankName}</strong></p>}
                    {paymentMethods.wire.accountName && <p>Account Name: {paymentMethods.wire.accountName}</p>}
                    {paymentMethods.wire.institutionNumber && <p>Institution #: {paymentMethods.wire.institutionNumber}</p>}
                    {paymentMethods.wire.transitNumber && <p>Transit #: {paymentMethods.wire.transitNumber}</p>}
                    {paymentMethods.wire.accountNumber && <p>Account #: {paymentMethods.wire.accountNumber}</p>}
                    {paymentMethods.wire.swiftCode && <p>SWIFT: {paymentMethods.wire.swiftCode}</p>}
                    {paymentMethods.wire.notes && <p className="text-slate-500 mt-1">{paymentMethods.wire.notes}</p>}
                  </div>
                )}

                {method === "cheque" && paymentMethods.cheque && (
                  <div className="text-sm space-y-1 pl-6">
                    {paymentMethods.cheque.payableTo && (
                      <p className="text-slate-700">Payable to: <strong>{paymentMethods.cheque.payableTo}</strong></p>
                    )}
                    {paymentMethods.cheque.mailingAddress && (
                      <p className="text-xs text-slate-600">Mail to: {paymentMethods.cheque.mailingAddress}</p>
                    )}
                    {paymentMethods.cheque.notes && (
                      <p className="text-xs text-slate-500">{paymentMethods.cheque.notes}</p>
                    )}
                  </div>
                )}

                {method === "eft" && paymentMethods.eft && (
                  <div className="text-xs space-y-1 pl-6 text-slate-700">
                    {paymentMethods.eft.institutionNumber && <p>Institution #: {paymentMethods.eft.institutionNumber}</p>}
                    {paymentMethods.eft.transitNumber && <p>Transit #: {paymentMethods.eft.transitNumber}</p>}
                    {paymentMethods.eft.accountNumber && <p>Account #: {paymentMethods.eft.accountNumber}</p>}
                    {paymentMethods.eft.notes && <p className="text-slate-500 mt-1">{paymentMethods.eft.notes}</p>}
                  </div>
                )}

                {method === "cash" && (
                  <p className="text-xs text-slate-500 pl-6">Contact the firm to arrange an in-person payment.</p>
                )}
              </div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const PayInvoicePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const isSuccess = location.pathname.endsWith("/success");

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const fetchInvoice = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await publicPayApi.getInvoice(token);
      setInvoice(res.data);
    } catch (err: any) {
      if (err.response?.status === 404 || err.response?.status === 410) {
        setError("not_found");
      } else {
        setError("unknown");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isSuccess) {
      fetchInvoice();
    } else {
      setLoading(false);
    }
  }, [isSuccess, fetchInvoice]);

  const handlePay = async () => {
    if (!token) return;
    try {
      setCheckoutLoading(true);
      setCheckoutError(null);
      const res = await publicPayApi.createCheckout(token);
      window.location.href = res.data.checkoutUrl;
    } catch {
      setCheckoutError("Unable to start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      <KavoaHeader />

      <div className="max-w-lg mx-auto px-6 py-10">
        {/* Success page */}
        {isSuccess && token && <SuccessView token={token} />}

        {/* Loading */}
        {!isSuccess && loading && <LoadingSkeleton />}

        {/* Error / invalid link */}
        {!isSuccess && !loading && error && <InvalidLinkView />}

        {/* Already paid */}
        {!isSuccess &&
          !loading &&
          !error &&
          invoice &&
          invoice.status === "paid" && <AlreadyPaidView invoice={invoice} />}

        {/* Main payment view */}
        {!isSuccess &&
          !loading &&
          !error &&
          invoice &&
          invoice.status !== "paid" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-5"
            >
              {/* Invoice card */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Header */}
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">
                        Invoice from
                      </p>
                      <h2 className="text-lg font-bold text-slate-800 mt-0.5">
                        {invoice.firmName}
                      </h2>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {invoice.invoiceNumber}
                      </span>
                    </div>
                  </div>
                  {invoice.dueAt && (
                    <p className="text-sm text-slate-400 mt-1">
                      Due {formatDate(invoice.dueAt)}
                    </p>
                  )}
                </div>

                {/* Line items */}
                <div className="border-t border-slate-100">
                  <div className="px-6 py-4 space-y-3">
                    {invoice.lineItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-start justify-between text-sm"
                      >
                        <span className="text-slate-600 pr-4 flex-1">
                          {item.description}
                        </span>
                        <span className="text-slate-800 font-medium whitespace-nowrap">
                          {formatCurrency(item.amountCents, invoice.currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-slate-100 px-6 py-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Subtotal</span>
                    <span className="text-slate-700 font-medium">
                      {formatCurrency(
                        invoice.subtotalCents ??
                          invoice.lineItems.reduce((sum, li) => sum + (li.amountCents || 0), 0),
                        invoice.currency
                      )}
                    </span>
                  </div>
                  {(invoice.discountCents || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Discount</span>
                      <span className="text-slate-700 font-medium">
                        -{formatCurrency(invoice.discountCents, invoice.currency)}
                      </span>
                    </div>
                  )}
                  {(invoice.taxCents || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Tax</span>
                      <span className="text-slate-700 font-medium">
                        {formatCurrency(invoice.taxCents, invoice.currency)}
                      </span>
                    </div>
                  )}
                  {((invoice.discountCents || 0) > 0 || (invoice.taxCents || 0) > 0) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Total</span>
                      <span className="text-slate-700 font-medium">
                        {formatCurrency(invoice.totalCents, invoice.currency)}
                      </span>
                    </div>
                  )}
                  {invoice.paidCents > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Payments received</span>
                      <span className="text-emerald-600 font-medium">
                        -{formatCurrency(invoice.paidCents, invoice.currency)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-slate-100">
                    <span className="text-slate-800 font-semibold">
                      Balance Due
                    </span>
                    <span
                      className="text-lg font-bold"
                      style={{ color: "var(--color-primary)" }}
                    >
                      {formatCurrency(
                        invoice.balanceDueCents,
                        invoice.currency
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Checkout error */}
              {checkoutError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {checkoutError}
                </motion.div>
              )}

              {/* Pay button or payment unavailable notice */}
              {invoice.paymentEnabled === false ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-center space-y-1">
                  <p className="text-sm font-semibold text-amber-800">
                    Online payment is not yet available
                  </p>
                  <p className="text-sm text-amber-700">
                    {invoice.firmName} is still setting up their payment processing. Please contact them directly to arrange payment.
                  </p>
                </div>
              ) : (
                <button
                  onClick={handlePay}
                  disabled={checkoutLoading}
                  className="w-full text-white rounded-xl py-3.5 font-bold text-base flex items-center justify-center gap-2 transition-colors disabled:opacity-70"
                  style={{
                    backgroundColor: checkoutLoading ? "var(--color-primary-hover)" : "var(--color-primary)",
                  }}
                  onMouseEnter={(e) => {
                    if (!checkoutLoading)
                      (e.target as HTMLButtonElement).style.backgroundColor =
                        "var(--color-primary-hover)";
                  }}
                  onMouseLeave={(e) => {
                    if (!checkoutLoading)
                      (e.target as HTMLButtonElement).style.backgroundColor =
                        "var(--color-primary)";
                  }}
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Redirecting to checkout...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5" />
                      Pay Now &mdash;{" "}
                      {formatCurrency(
                        invoice.balanceDueCents,
                        invoice.currency
                      )}
                    </>
                  )}
                </button>
              )}

              {/* Trust badges */}
              {invoice.paymentEnabled !== false && (
                <div className="flex flex-col items-center gap-2 pt-1">
                  <div className="flex items-center gap-1.5 text-slate-400">
                    <img
                      src="https://cdn.brandfetch.io/stripe.com/w/32/h/32"
                      alt="Stripe"
                      className="h-4 opacity-50"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <span className="text-xs">Secured by Stripe</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-400">
                    <Shield className="h-3.5 w-3.5" />
                    <span className="text-xs">256-bit encryption</span>
                  </div>
                </div>
              )}

              {/* Alternative payment methods */}
              <AlternativePaymentMethods
                paymentMethods={invoice.paymentMethods}
                preferredMethod={invoice.preferredMethod}
              />
            </motion.div>
          )}
      </div>
    </div>
  );
};

export default PayInvoicePage;
